import { getDesktopBridge, isDesktopPlatform } from '@/lib/desktop/bridge';

import { isNativePlatform } from './platform';

export interface SupabaseStorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

// The plugin's default Keychain class is `whenUnlocked`, which evicts the
// class key whenever the device locks - so an iOS prewarm or a pre-first-
// unlock launch reads nothing, boots GoTrue sessionless, and shows the login
// screen over an intact session. `afterFirstUnlockThisDeviceOnly` keeps the
// session readable from first unlock after boot until power-off. Stated
// tradeoff: an AFU-state forensic extraction can read session material while
// the device is locked; compensations are remote revocation, the biometric
// app lock, and JWT expiry. ThisDeviceOnly = never in iCloud Keychain, not
// restorable from backup - a restored device starts signed out, by design.
let keychainAccessReady: Promise<boolean> | null = null;

// Never return or await the plugin proxy itself: resolving it as an async
// return value makes the engine probe `.then`, and the Capacitor proxy
// forwards EVERY property access to the native bridge - on web that probe
// throws UNIMPLEMENTED as an unhandled rejection.
//
// Best-effort by design: a failed pin means new writes ride the plugin
// default - exactly yesterday's behaviour - while failing hard here would
// turn a cosmetic Keychain hiccup into a login outage. The memo resets on
// failure so the next storage call retries rather than awaiting a
// permanently rejected promise.
async function ensureKeychainAccess(): Promise<boolean> {
  const { SecureStorage, KeychainAccess } = await import('@aparajita/capacitor-secure-storage');
  keychainAccessReady ??= SecureStorage.setDefaultKeychainAccess(
    KeychainAccess.afterFirstUnlockThisDeviceOnly,
  )
    .then(() => true)
    .catch(() => {
      keychainAccessReady = null;
      return false;
    });
  return await keychainAccessReady;
}

// Accessibility applies only to NEW writes; an entry created before this
// shipped keeps `whenUnlocked` forever unless rewritten. The non-secret marker
// makes that migration once-per-install rather than a Keychain write on every
// process launch. Losing web data merely causes one safe repeat migration.
const KEYCHAIN_CLASS_MIGRATION_PREFIX = 'pp-keychain-afu-v1:';
const rewrittenKeys = new Set<string>();
const PKCE_VERIFIER_SUFFIX = '-code-verifier';
const deferredPkceVerifierRemovals = new Set<string>();
let pkceVerifierProtectionActive = false;
let pkceVerifierFinish: Promise<void> | null = null;

function keychainClassMarker(key: string): string {
  return `${KEYCHAIN_CLASS_MIGRATION_PREFIX}${key}`;
}

function hasMigratedKeychainClass(key: string): boolean {
  try {
    return globalThis.localStorage.getItem(keychainClassMarker(key)) === 'true';
  } catch {
    return false;
  }
}

function markKeychainClassMigrated(key: string): void {
  try {
    globalThis.localStorage.setItem(keychainClassMarker(key), 'true');
  } catch {
    // A blocked marker only costs another best-effort rewrite next launch.
  }
}

function clearKeychainClassMarker(key: string): void {
  try {
    globalThis.localStorage.removeItem(keychainClassMarker(key));
  } catch {
    // Secure deletion is load-bearing; deleting this non-secret marker is not.
  }
}

async function removeNativeSecureStorageItem(key: string): Promise<void> {
  const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
  await ensureKeychainAccess();
  rewrittenKeys.delete(key);
  await SecureStorage.removeItem(key);
  clearKeychainClassMarker(key);
}

/** Keep auth-js session cleanup from deleting a cold-callback PKCE verifier. */
export function beginNativePkceVerifierProtection(): void {
  if (isNativePlatform()) {
    pkceVerifierProtectionActive = true;
  }
}

async function flushDeferredPkceVerifierRemovals(): Promise<void> {
  while (deferredPkceVerifierRemovals.size > 0) {
    const key = [...deferredPkceVerifierRemovals][0];
    if (key === undefined) {
      break;
    }
    await removeNativeSecureStorageItem(key);
    deferredPkceVerifierRemovals.delete(key);
  }
  pkceVerifierProtectionActive = false;
}

/** Remove protected verifiers after callback exchange; failures remain retryable. */
export async function finishNativePkceVerifierProtection(): Promise<void> {
  if (!pkceVerifierProtectionActive) {
    return;
  }
  pkceVerifierFinish ??= flushDeferredPkceVerifierRemovals().finally(() => {
    pkceVerifierFinish = null;
  });
  await pkceVerifierFinish;
}

const secureStorageAdapter: SupabaseStorageAdapter = {
  getItem: async (key: string) => {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    const accessPinned = await ensureKeychainAccess();
    const value = await SecureStorage.getItem(key);
    const classMigrated = hasMigratedKeychainClass(key);
    if (value !== null && accessPinned && !rewrittenKeys.has(key) && !classMigrated) {
      // Marked before the write to keep a concurrent read from rewriting
      // twice; unmarked on failure so the next read retries.
      rewrittenKeys.add(key);
      try {
        await SecureStorage.setItem(key, value);
        markKeychainClassMigrated(key);
      } catch {
        // Best-effort: the read is the load-bearing half; the entry keeps its
        // old class until the next rewrite opportunity.
        rewrittenKeys.delete(key);
      }
    } else if (value !== null && classMigrated) {
      rewrittenKeys.add(key);
    }
    return value;
  },
  setItem: async (key: string, value: string) => {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    const accessPinned = await ensureKeychainAccess();
    await SecureStorage.setItem(key, value);
    if (accessPinned) {
      rewrittenKeys.add(key);
      markKeychainClassMigrated(key);
    } else {
      rewrittenKeys.delete(key);
      clearKeychainClassMarker(key);
    }
  },
  removeItem: async (key: string) => {
    if (pkceVerifierProtectionActive && key.endsWith(PKCE_VERIFIER_SUFFIX)) {
      deferredPkceVerifierRemovals.add(key);
      return;
    }
    await removeNativeSecureStorageItem(key);
  },
};

export function __resetKeychainAccessForTests(): void {
  keychainAccessReady = null;
  rewrittenKeys.clear();
  deferredPkceVerifierRemovals.clear();
  pkceVerifierProtectionActive = false;
  pkceVerifierFinish = null;
}

const unavailableDesktopStorageAdapter: SupabaseStorageAdapter = {
  getItem: async () => await Promise.resolve<string | null>(null),
  setItem: async () =>
    await Promise.reject(new Error('Desktop secure auth storage is unavailable')),
  removeItem: async () => await Promise.resolve(),
};

// Durable record of the last explicit persistence choice. The in-memory flag
// resets on every renderer reload, but auth callbacks (OAuth deep link, magic
// link) can complete in a fresh renderer that never revisits the login form —
// without this record such a completion would silently write an opted-out
// session to the Keychain. Absence of the record means "persist" (the desktop
// default); the login form's own sessionStorage mirror only pre-fills the
// checkbox and is never read for enforcement.
const DESKTOP_AUTH_PERSISTENCE_KEY = 'pp-desktop-auth-persistence';

function readStoredDesktopAuthPersistence(): boolean {
  try {
    return globalThis.localStorage.getItem(DESKTOP_AUTH_PERSISTENCE_KEY) !== 'false';
  } catch {
    return true;
  }
}

let persistDesktopAuthSession = readStoredDesktopAuthPersistence();
const volatileDesktopAuthStorage = new Map<string, string>();

/** Controls whether future desktop auth writes go to Keychain or process memory. */
export function configureDesktopAuthPersistence(rememberSession: boolean): void {
  if (persistDesktopAuthSession !== rememberSession) {
    volatileDesktopAuthStorage.clear();
  }
  persistDesktopAuthSession = rememberSession;
  try {
    globalThis.localStorage.setItem(DESKTOP_AUTH_PERSISTENCE_KEY, String(rememberSession));
  } catch {
    // Storage unavailable (private mode / test env): the in-memory flag still
    // governs this renderer session; only reload survival is lost.
  }
}

function createDesktopStorageAdapter(storage: SupabaseStorageAdapter): SupabaseStorageAdapter {
  return {
    getItem: async (key: string) => {
      if (!persistDesktopAuthSession) {
        return volatileDesktopAuthStorage.get(key) ?? null;
      }
      return await storage.getItem(key);
    },
    setItem: async (key: string, value: string) => {
      if (persistDesktopAuthSession) {
        volatileDesktopAuthStorage.delete(key);
        await storage.setItem(key, value);
        return;
      }
      await storage.removeItem(key);
      volatileDesktopAuthStorage.set(key, value);
    },
    removeItem: async (key: string) => {
      volatileDesktopAuthStorage.delete(key);
      await storage.removeItem(key);
    },
  };
}

export function __resetDesktopAuthPersistenceForTests(): void {
  persistDesktopAuthSession = true;
  volatileDesktopAuthStorage.clear();
  try {
    globalThis.localStorage.removeItem(DESKTOP_AUTH_PERSISTENCE_KEY);
  } catch {
    // Storage unavailable: nothing durable to clear.
  }
}

export function getSupabaseAuthStorage(): SupabaseStorageAdapter {
  const desktop = getDesktopBridge();
  if (desktop) {
    return createDesktopStorageAdapter(desktop.authStorage);
  }
  if (isDesktopPlatform()) {
    return unavailableDesktopStorageAdapter;
  }
  if (isNativePlatform()) {
    return secureStorageAdapter;
  }

  return globalThis.localStorage;
}
